// 📝 수동 신청 등록 다이얼로그
// [배경] 거래처로부터 엑셀로 받은 주문을 관리자가 직접 시스템에 등록
// [핵심] 이름+전화번호(필수) + 기기 추가(기존 AddDeviceDialog 재사용)
// 주소/계좌는 나중에 고객이 직접 입력 (토큰+뒷4자리 인증)

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Divider,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  PhoneAndroid as PhoneIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';
// 🔥 기존 사용자 포탈의 AddDeviceDialog 재사용
import AddDeviceDialog from '../../components/AddDeviceDialog';
import { DeviceGrade } from '../../../types/gradeSystem';

// ═══════════════════════════════════════════════════════════
// 타입 정의
// ═══════════════════════════════════════════════════════════

interface ManualApplicationDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (applicationId: string, applicationNumber: number) => void;
  adminEmail: string;  // 현재 로그인한 관리자 이메일
}

/** 기기 정보 (AddDeviceDialog에서 반환되는 형식) */
interface DeviceItem {
  deviceType: string;
  deviceModel: string;
  userSelectedGrade?: DeviceGrade;
  estimatedPrice?: number | null;
}

/** 폼 데이터 */
interface FormData {
  userName: string;
  phoneNumber: string;
  partnerName: string;
  orderNumber: string;
  memo: string;
  devices: DeviceItem[];
  sendInfoRequest: boolean;
}

// ═══════════════════════════════════════════════════════════
// 컴포넌트
// ═══════════════════════════════════════════════════════════

const ManualApplicationDialog: React.FC<ManualApplicationDialogProps> = ({
  open,
  onClose,
  onSuccess,
  adminEmail,
}) => {
  // 📌 폼 상태
  const [formData, setFormData] = useState<FormData>({
    userName: '',
    phoneNumber: '',
    partnerName: '아이즈모바일',  // 🔧 거래처명 기본값
    orderNumber: '',
    memo: '',
    devices: [],
    sendInfoRequest: true,
  });
  
  // 📌 UI 상태
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeviceDialog, setShowDeviceDialog] = useState(false);
  // 🔧 거래처 정보 섹션 항상 열려 있음 (true 고정)
  const partnerExpanded = true;

  // 📌 고객명 필드 자동 포커스를 위한 ref
  const userNameInputRef = useRef<HTMLInputElement>(null);

  // 🔧 다이얼로그 열릴 때 고객명 필드에 포커스
  useEffect(() => {
    if (open) {
      // setTimeout으로 다이얼로그 애니메이션 완료 후 포커스
      const timer = setTimeout(() => {
        userNameInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // ═══════════════════════════════════════════════════════════
  // 유틸리티 함수
  // ═══════════════════════════════════════════════════════════

  /**
   * 전화번호 자동 포맷팅 (하이픈 추가)
   */
  const formatPhoneNumber = (value: string): string => {
    const numbers = value.replace(/[^0-9]/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  };

  /**
   * 전화번호에서 맨 마지막 4자리 추출
   */
  const getPhoneLast4 = (phone: string): string => {
    const numbersOnly = phone.replace(/[^0-9]/g, '');
    return numbersOnly.slice(-4);
  };

  // ═══════════════════════════════════════════════════════════
  // 이벤트 핸들러
  // ═══════════════════════════════════════════════════════════

  /** 텍스트 입력 핸들러 */
  const handleInputChange = (field: keyof FormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value = e.target.value;
    if (field === 'phoneNumber') {
      value = formatPhoneNumber(value);
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null); // 입력 시 에러 초기화
  };

  /**
   * 🔥 기기 추가 핸들러 - AddDeviceDialog에서 콜백으로 호출
   */
  const handleAddDevice = useCallback((device: {
    deviceType: string;
    deviceModel: string;
    userSelectedGrade?: DeviceGrade;
    estimatedPrice?: number | null;
  }) => {
    setFormData(prev => ({
      ...prev,
      devices: [...prev.devices, device as DeviceItem],
    }));
    setShowDeviceDialog(false);
  }, []);

  /** 기기 삭제 핸들러 */
  const handleRemoveDevice = (index: number) => {
    setFormData(prev => ({
      ...prev,
      devices: prev.devices.filter((_, i) => i !== index),
    }));
  };

  // ═══════════════════════════════════════════════════════════
  // 유효성 검사
  // ═══════════════════════════════════════════════════════════

  const validateForm = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    // 고객명 검사
    if (!formData.userName.trim()) {
      errors.push('고객명을 입력해주세요.');
    }
    
    // 전화번호 검사 (최소 10자리)
    const phoneClean = formData.phoneNumber.replace(/-/g, '');
    if (!phoneClean || phoneClean.length < 10) {
      errors.push('올바른 전화번호를 입력해주세요. (최소 10자리)');
    }
    
    // 기기 검사
    if (formData.devices.length === 0) {
      errors.push('최소 1개 이상의 기기를 추가해주세요.');
    }
    
    return { isValid: errors.length === 0, errors };
  };

  // ═══════════════════════════════════════════════════════════
  // 총 예상 금액 계산
  // ═══════════════════════════════════════════════════════════

  const totalEstimatedPrice = formData.devices.reduce(
    (sum, d) => sum + (d.estimatedPrice || 0),
    0
  );

  // ═══════════════════════════════════════════════════════════
  // 제출 핸들러
  // ═══════════════════════════════════════════════════════════

  const handleSubmit = async () => {
    // 유효성 검사
    const validation = validateForm();
    if (!validation.isValid) {
      setError(validation.errors.join('\n'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 전화번호 정규화 (하이픈 제거)
      const phoneClean = formData.phoneNumber.replace(/-/g, '');
      
      // API 호출
      const response = await fetch(
        'https://us-central1-refurb-buy.cloudfunctions.net/api/api/applications/manual',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_name: formData.userName.trim(),
            phone_number: phoneClean,
            devices: formData.devices.map(d => ({
              deviceType: d.deviceType,
              deviceModel: d.deviceModel,
              userSelectedGrade: d.userSelectedGrade || null,
              estimatedPrice: d.estimatedPrice ?? null,
            })),
            partner_info: formData.partnerName.trim() ? {
              partner_name: formData.partnerName.trim(),
              order_number: formData.orderNumber.trim() || undefined,
              memo: formData.memo.trim() || undefined,
            } : undefined,
            send_info_request: formData.sendInfoRequest,
            admin_email: adminEmail,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || '수동 신청 등록에 실패했습니다.');
      }

      // 성공 콜백
      onSuccess(result.application_id, result.application_number);
      handleClose();
      
    } catch (err: any) {
      console.error('❌ 수동 신청 등록 실패:', err);
      setError(err.message || '오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════
  // 다이얼로그 닫기 (상태 초기화)
  // ═══════════════════════════════════════════════════════════

  const handleClose = () => {
    if (loading) return; // 로딩 중에는 닫기 방지
    
    setFormData({
      userName: '',
      phoneNumber: '',
      partnerName: '아이즈모바일',  // 🔧 거래처명 기본값 유지
      orderNumber: '',
      memo: '',
      devices: [],
      sendInfoRequest: true,
    });
    setError(null);
    // 🔧 partnerExpanded는 항상 true로 고정이므로 상태 초기화 불필요
    onClose();
  };

  // ═══════════════════════════════════════════════════════════
  // 렌더링
  // ═══════════════════════════════════════════════════════════

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ 
          sx: { 
            borderRadius: 2,
            maxHeight: '90vh',
          } 
        }}
      >
        {/* 헤더 */}
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 1.5,
        }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6" fontWeight="bold">
              📝 수동 신청 등록
            </Typography>
            <Chip 
              label="거래처 유입" 
              size="small" 
              color="secondary" 
              variant="outlined" 
            />
          </Stack>
          <IconButton onClick={handleClose} size="small" disabled={loading}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 2.5 }}>
          {/* 📌 에러 메시지 */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 2.5 }} 
              onClose={() => setError(null)}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {error}
              </Typography>
            </Alert>
          )}

          {/* ═══════════════════════════════════════════════════════════ */}
          {/* 📌 고객 정보 섹션 (필수) */}
          {/* ═══════════════════════════════════════════════════════════ */}
          <Box sx={{ mb: 3 }}>
            <Typography 
              variant="subtitle2" 
              color="primary" 
              sx={{ 
                mb: 1.5, 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                fontWeight: 600,
              }}
            >
              <PersonIcon fontSize="small" /> 고객 정보 (필수)
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="고객명"
                value={formData.userName}
                onChange={handleInputChange('userName')}
                required
                fullWidth
                size="small"
                placeholder="홍길동"
                disabled={loading}
                error={!!error && !formData.userName.trim()}
                inputRef={userNameInputRef}  // 🔧 자동 포커스를 위한 ref
              />
              <TextField
                label="전화번호"
                value={formData.phoneNumber}
                onChange={handleInputChange('phoneNumber')}
                required
                fullWidth
                size="small"
                placeholder="010-1234-5678"
                inputProps={{ maxLength: 13 }}
                disabled={loading}
                error={!!error && formData.phoneNumber.replace(/-/g, '').length < 10}
                helperText={
                  formData.phoneNumber && 
                  `뒷 4자리: ${getPhoneLast4(formData.phoneNumber) || '-'}`
                }
              />
            </Stack>
          </Box>

          {/* ═══════════════════════════════════════════════════════════ */}
          {/* 📌 거래처 정보 섹션 (선택, 접이식) */}
          {/* ═══════════════════════════════════════════════════════════ */}
          {/* 🔧 거래처 정보 섹션 - 항상 열려 있음 (접기 기능 비활성화) */}
          <Accordion
            expanded={partnerExpanded}
            sx={{ 
              mb: 3, 
              '&:before': { display: 'none' },
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px !important',
              '&.Mui-expanded': {
                margin: 0,
                mb: 3,
              },
            }}
          >
            <AccordionSummary 
              sx={{ 
                minHeight: 48,
                '&.Mui-expanded': { minHeight: 48 },
                cursor: 'default',  // 🔧 클릭 커서 비활성화
                '&:hover': { cursor: 'default' },
              }}
            >
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  color: 'text.secondary',
                }}
              >
                <BusinessIcon fontSize="small" /> 거래처 정보 (선택)
                {formData.partnerName && (
                  <Chip 
                    label={formData.partnerName} 
                    size="small" 
                    sx={{ ml: 1 }} 
                  />
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="거래처명"
                    value={formData.partnerName}
                    onChange={handleInputChange('partnerName')}
                    fullWidth
                    size="small"
                    placeholder="OO통신"
                    disabled={loading}
                  />
                  <TextField
                    label="주문번호"
                    value={formData.orderNumber}
                    onChange={handleInputChange('orderNumber')}
                    fullWidth
                    size="small"
                    placeholder="ORD-2024-001"
                    disabled={loading}
                  />
                </Stack>
                <TextField
                  label="메모"
                  value={formData.memo}
                  onChange={handleInputChange('memo')}
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  placeholder="12월 1차 일괄 접수분"
                  disabled={loading}
                />
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* ═══════════════════════════════════════════════════════════ */}
          {/* 📌 기기 목록 섹션 */}
          {/* ═══════════════════════════════════════════════════════════ */}
          <Box sx={{ mb: 3 }}>
            <Typography 
              variant="subtitle2" 
              color="primary" 
              sx={{ 
                mb: 1.5, 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                fontWeight: 600,
              }}
            >
              <PhoneIcon fontSize="small" /> 기기 목록
              {formData.devices.length > 0 && (
                <Chip 
                  label={`${formData.devices.length}대`} 
                  size="small" 
                  color="primary"
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
            
            {/* 기기 리스트 - 고급 카드 스타일 */}
            {formData.devices.length > 0 ? (
              <Box sx={{ mb: 1.5 }}>
                {formData.devices.map((device, index) => {
                  // 📌 예상 시세 유무 확인
                  const hasEstimatedPrice = device.estimatedPrice != null && device.estimatedPrice > 0;
                  
                  // 📌 기기 정보 파싱 (모델명 - 용량)
                  const deviceParts = device.deviceModel.split(' - ');
                  const modelName = deviceParts[0] || device.deviceModel;
                  const storage = deviceParts.length > 1 ? deviceParts[1] : '';
                  
                  // 📌 등급 색상 매핑
                  const gradeColorMap: Record<string, { fg: string; bg: string }> = {
                    A: { fg: '#10b981', bg: 'rgba(16,185,129,0.12)' },
                    B: { fg: '#3b82f6', bg: 'rgba(59,130,246,0.12)' },
                    C: { fg: '#f59e0b', bg: 'rgba(245,158,11,0.14)' },
                    D: { fg: '#ef4444', bg: 'rgba(239,68,68,0.14)' },
                  };
                  const gradeColor = device.userSelectedGrade 
                    ? gradeColorMap[device.userSelectedGrade] || gradeColorMap.B 
                    : null;

                  return (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        mb: index < formData.devices.length - 1 ? 1.5 : 0,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: hasEstimatedPrice 
                          ? 'rgba(16, 185, 129, 0.3)' 
                          : 'rgba(156, 163, 175, 0.3)',
                        background: hasEstimatedPrice
                          ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.04) 0%, rgba(5, 150, 105, 0.02) 100%)'
                          : 'linear-gradient(135deg, rgba(249, 250, 251, 1) 0%, rgba(243, 244, 246, 1) 100%)',
                        transition: 'all 0.2s ease',
                        position: 'relative',
                        '&:hover': {
                          borderColor: hasEstimatedPrice 
                            ? 'rgba(16, 185, 129, 0.5)' 
                            : 'rgba(156, 163, 175, 0.5)',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                        }
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        {/* 기기 정보 영역 */}
                        <Box sx={{ flex: 1 }}>
                          {/* 모델명 */}
                          <Typography 
                            variant="body1" 
                            fontWeight={600}
                            sx={{ 
                              color: '#1f2937',
                              mb: 0.5,
                              fontSize: '0.95rem',
                            }}
                          >
                            {modelName}
                          </Typography>
                          
                          {/* 저장 용량 & 카테고리 */}
                          <Stack 
                            direction="row" 
                            spacing={0.75} 
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ mb: 1 }}
                          >
                            <Chip 
                              label={device.deviceType} 
                              size="small" 
                              sx={{ 
                                fontSize: 11,
                                height: 22,
                                bgcolor: 'rgba(99, 102, 241, 0.08)',
                                color: '#6366f1',
                                fontWeight: 600,
                              }}
                            />
                            {storage && (
                              <Chip 
                                label={storage} 
                                size="small" 
                                sx={{ 
                                  fontSize: 11,
                                  height: 22,
                                  bgcolor: 'rgba(59, 130, 246, 0.08)',
                                  color: '#3b82f6',
                                  fontWeight: 600,
                                }}
                              />
                            )}
                            {device.userSelectedGrade && gradeColor && (
                              <Chip 
                                label={`${device.userSelectedGrade}등급`} 
                                size="small" 
                                sx={{ 
                                  fontSize: 11,
                                  height: 22,
                                  bgcolor: gradeColor.bg,
                                  color: gradeColor.fg,
                                  fontWeight: 700,
                                }}
                              />
                            )}
                          </Stack>
                          
                          {/* 💰 예상 시세 표시 영역 */}
                          {hasEstimatedPrice ? (
                            <Box 
                              sx={{ 
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                gap: 0.5,
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1.5,
                                bgcolor: 'rgba(16, 185, 129, 0.1)',
                                border: '1px solid rgba(16, 185, 129, 0.2)',
                              }}
                            >
                              <AttachMoneyIcon sx={{ fontSize: 14, color: '#059669' }} />
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: '#059669', 
                                  fontWeight: 700,
                                  fontSize: '0.85rem',
                                }}
                              >
                                예상 시세: {device.estimatedPrice!.toLocaleString()}원
                              </Typography>
                            </Box>
                          ) : (
                            <Chip
                              icon={<ScheduleIcon sx={{ fontSize: 14, color: '#6b7280 !important' }} />}
                              label="검수후 알림 예정"
                              size="small"
                              sx={{
                                height: 26,
                                fontSize: 11,
                                fontWeight: 600,
                                bgcolor: 'rgba(107, 114, 128, 0.08)',
                                color: '#6b7280',
                                border: '1px dashed rgba(107, 114, 128, 0.3)',
                                '& .MuiChip-icon': {
                                  ml: 0.5,
                                },
                              }}
                            />
                          )}
                        </Box>
                        
                        {/* 삭제 버튼 */}
                        <IconButton 
                          size="small" 
                          onClick={() => handleRemoveDevice(index)}
                          disabled={loading}
                          sx={{
                            color: 'error.main',
                            ml: 1,
                            bgcolor: 'rgba(239, 68, 68, 0.08)',
                            '&:hover': {
                              bgcolor: 'rgba(239, 68, 68, 0.15)',
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Alert 
                severity="info" 
                sx={{ 
                  mb: 1.5,
                  borderRadius: 2,
                  bgcolor: 'rgba(99, 102, 241, 0.04)',
                  border: '1px dashed rgba(99, 102, 241, 0.3)',
                  '& .MuiAlert-icon': {
                    color: '#6366f1',
                  },
                }}
                icon={<PhoneIcon />}
              >
                <Typography variant="body2" fontWeight={500}>
                  추가된 기기가 없습니다. 아래 버튼을 눌러 기기를 추가해주세요.
                </Typography>
              </Alert>
            )}

            {/* 기기 추가 버튼 + 총 예상 금액 */}
            <Stack 
              direction="row" 
              justifyContent="space-between" 
              alignItems="center"
            >
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setShowDeviceDialog(true)}
                size="small"
                disabled={loading}
              >
                기기 추가
              </Button>
              {totalEstimatedPrice > 0 && (
                <Typography variant="body2" color="text.secondary">
                  총 예상 금액: <strong style={{ color: '#2e7d32' }}>
                    {totalEstimatedPrice.toLocaleString()}원
                  </strong>
                </Typography>
              )}
            </Stack>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* ═══════════════════════════════════════════════════════════ */}
          {/* 📌 안내 문구 */}
          {/* ═══════════════════════════════════════════════════════════ */}
          <Alert 
            severity="info" 
            icon={false} 
            sx={{ 
              mb: 2,
              bgcolor: 'primary.50',
              '& .MuiAlert-message': { width: '100%' },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
              💡 주소/계좌 정보 안내
            </Typography>
            <Typography variant="caption" color="text.secondary">
              주소와 계좌 정보는 고객에게 알림톡으로 입력 링크를 발송하여 직접 입력받습니다.
              <br />
              고객은 전화번호 뒷 4자리로 본인 확인 후 정보를 입력합니다.
            </Typography>
          </Alert>

          {/* ═══════════════════════════════════════════════════════════ */}
          {/* 📌 알림톡 발송 옵션 */}
          {/* ═══════════════════════════════════════════════════════════ */}
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.sendInfoRequest}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  sendInfoRequest: e.target.checked 
                }))}
                disabled={loading}
              />
            }
            label={
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <SendIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  등록 후 즉시 "정보 입력 요청" 알림톡 발송
                </Typography>
              </Stack>
            }
          />
        </DialogContent>

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* 📌 액션 버튼 */}
        {/* ═══════════════════════════════════════════════════════════ */}
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            color="inherit"
          >
            취소
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || formData.devices.length === 0}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          >
            {loading ? '등록 중...' : '등록하기'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* 📌 기기 추가 다이얼로그 (기존 사용자 포탈 컴포넌트 재사용) */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <AddDeviceDialog
        open={showDeviceDialog}
        onClose={() => setShowDeviceDialog(false)}
        onAdd={handleAddDevice}
        mode="add"
      />
    </>
  );
};

export default ManualApplicationDialog;

